import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Interview, Markdown } from '../../components/coaching/JumpstartStudio';
import {
  MessageSquarePlus, Briefcase, HeartHandshake, ChevronDown, Loader2, Wand2,
  CheckCircle2, User,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API, withCredentials: true });

/* one back-to-back interview section (business or nonprofit) */
function Section({ tone, title, subtitle, icon: Icon, session, framework, onSaveAnswer, onGenerate, generating }) {
  const answers = session?.answers || {};
  const total = framework.flatMap((s) => s.phases.flatMap((p) => p.questions)).length;
  const answered = framework.flatMap((s) => s.phases.flatMap((p) => p.questions)).filter((q) => (answers[q.id] || '').trim()).length;
  const ready = session?.status === 'completed' && session?.deliverable;
  const accent = tone === 'business' ? '#1D4ED8' : '#C026D3';
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden" data-testid={`combined-section-${tone}`}>
      <div className="h-1.5" style={{ background: accent }} />
      <div className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: accent }}><Icon size={18} /></span>
            <div>
              <h2 className="text-base font-black text-[#0A0F1C]">{title}</h2>
              <p className="text-xs text-[#64748B]">{subtitle}</p>
            </div>
          </div>
          <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#F1F5F9] text-[#475569]" data-testid={`combined-progress-${tone}`}>{answered}/{total} answered</span>
        </div>

        <div className="mt-4">
          {ready ? (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid={`combined-blueprint-${tone}`}><Markdown text={session.deliverable.markdown} /></div>
          ) : (
            <Interview session={session} framework={framework} onSaveAnswer={onSaveAnswer} key={session?.updated_at} />
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button type="button" onClick={onGenerate} disabled={generating} data-testid={`combined-generate-${tone}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-bold text-sm disabled:opacity-70" style={{ background: accent }}>
            {generating ? <><Loader2 size={16} className="animate-spin" /> Building blueprint…</>
              : ready ? <><Wand2 size={16} /> Regenerate blueprint</>
              : <><Wand2 size={16} /> Generate {tone === 'business' ? 'business' : 'nonprofit'} blueprint</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CombinedInterview() {
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [biz, setBiz] = useState(null);
  const [npf, setNpf] = useState(null);
  const [fwBiz, setFwBiz] = useState([]);
  const [fwNpf, setFwNpf] = useState([]);
  const [loading, setLoading] = useState(false);
  const [genBiz, setGenBiz] = useState(false);
  const [genNpf, setGenNpf] = useState(false);
  const pollBiz = useRef(null);
  const pollNpf = useRef(null);

  useEffect(() => {
    api.get('/api/admin/clarity-hub/people').then((r) => {
      setPeople(r.data.people || []);
      setSelected((cur) => cur || (r.data.people || [])[0] || null);
    }).catch((e) => toast.error('Could not load people: ' + (e.response?.data?.detail || e.message)));
  }, []);
  useEffect(() => () => { [pollBiz, pollNpf].forEach((r) => r.current && clearInterval(r.current)); }, []);

  const loadFramework = useCallback(async () => {
    const fw = await api.get('/api/coaching/framework?flow=deep_dive');
    setFwBiz(fw.data.stages || []); setFwNpf(fw.data.stages || []);
  }, []);

  const ensure = useCallback(async (personKey) => {
    setLoading(true); setBiz(null); setNpf(null);
    try {
      await loadFramework();
      const { data } = await api.post(`/api/admin/combined-interview/ensure?person=${encodeURIComponent(personKey)}`);
      setBiz(data.business); setNpf(data.nonprofit);
    } catch (e) { toast.error('Could not set up interview: ' + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  }, [loadFramework]);

  useEffect(() => { if (selected) ensure(selected.key); }, [selected, ensure]);

  const saveAnswer = (which) => async (qid, val) => {
    const s = which === 'business' ? biz : npf;
    if (!s) return;
    const prev = (s.answers || {})[qid] || '';
    if (prev === val) return;
    const setter = which === 'business' ? setBiz : setNpf;
    setter((cur) => ({ ...cur, answers: { ...(cur.answers || {}), [qid]: val } }));
    try { await api.patch(`/api/coaching/sessions/${s.session_id}`, { answers: { [qid]: val } }); }
    catch { toast.error('Save failed'); }
  };

  const generate = (which) => async () => {
    const s = which === 'business' ? biz : npf;
    if (!s) return;
    const setGen = which === 'business' ? setGenBiz : setGenNpf;
    const setDoc = which === 'business' ? setBiz : setNpf;
    const pollRef = which === 'business' ? pollBiz : pollNpf;
    setGen(true);
    try {
      await api.post(`/api/coaching/sessions/${s.session_id}/generate`);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/api/coaching/sessions/${s.session_id}`);
          if (data.status === 'completed' && data.deliverable) {
            clearInterval(pollRef.current); pollRef.current = null;
            setDoc(data); setGen(false); toast.success(`${which === 'business' ? 'Business' : 'Nonprofit'} blueprint ready`);
          } else if (data.generation_error) {
            clearInterval(pollRef.current); pollRef.current = null;
            setGen(false); toast.error(data.generation_error);
          }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (e) { setGen(false); toast.error(e?.response?.data?.detail || 'Could not start generation'); }
  };

  return (
    <AdminLayout title="Combined Interview">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="combined-interview-page">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1D4ED8] to-fuchsia-500 flex items-center justify-center text-white"><MessageSquarePlus size={20} /></span>
            <div>
              <h1 className="text-2xl font-black text-[#0A0F1C] tracking-tight">Combined Interview</h1>
              <p className="text-sm text-[#64748B]">Run someone's Business Jumpstart and Nonprofit Launch questions back-to-back in one sitting.</p>
            </div>
          </div>
          <div className="relative">
            <button type="button" onClick={() => setPickerOpen((v) => !v)} data-testid="combined-person-picker" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#E2E8F0] bg-white hover:border-[#1D4ED8]">
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-black ${selected?.key === 'self' ? 'bg-[#0A0F1C]' : 'bg-[#1D4ED8]'}`}>{selected?.key === 'self' ? 'ME' : (selected?.name || '?').slice(0, 1).toUpperCase()}</span>
              <span className="text-left">
                <span className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] leading-none">Interviewing</span>
                <span className="block text-sm font-bold text-[#0A0F1C] leading-tight">{selected?.name || 'Select…'}</span>
              </span>
              <ChevronDown size={16} className={`text-[#94A3B8] transition-transform ${pickerOpen ? 'rotate-180' : ''}`} />
            </button>
            {pickerOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-[#E2E8F0] rounded-xl shadow-lg z-20 overflow-hidden max-h-80 overflow-y-auto" data-testid="combined-person-menu">
                {people.map((p) => (
                  <button type="button" key={p.key} onClick={() => { setSelected(p); setPickerOpen(false); }} data-testid={`combined-person-${p.key}`}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-slate-50 ${selected?.key === p.key ? 'bg-[#EFF6FF]' : ''}`}>
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-black shrink-0 ${p.key === 'self' ? 'bg-[#0A0F1C]' : 'bg-[#1D4ED8]'}`}>{p.key === 'self' ? 'ME' : (p.name || '?').slice(0, 1).toUpperCase()}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-bold text-[#0A0F1C] truncate">{p.name}</span>
                      {p.email && <span className="block text-[11px] text-[#94A3B8] truncate">{p.email}</span>}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="mt-10 text-center text-sm text-[#64748B]" data-testid="combined-loading"><Loader2 className="animate-spin inline mr-2" size={18} /> Setting up the interview…</div>
        ) : (biz && npf) ? (
          <div className="space-y-6" data-testid="combined-sections">
            <div className="rounded-xl border border-[#BFDBFE] bg-[#EFF6FF] p-3.5 text-xs text-[#1E40AF] flex items-center gap-2" data-testid="combined-intro">
              <CheckCircle2 size={15} /> Answers save automatically. Fill the business side first, then the nonprofit side — generate each blueprint when ready.
            </div>
            <Section tone="business" title="1 · Business Jumpstart" subtitle="7-Stage discovery for the for-profit venture" icon={Briefcase}
              session={biz} framework={fwBiz} onSaveAnswer={saveAnswer('business')} onGenerate={generate('business')} generating={genBiz} />
            <Section tone="nonprofit" title="2 · Nonprofit Launch" subtitle="Same 7-stage discovery for the nonprofit side" icon={HeartHandshake}
              session={npf} framework={fwNpf} onSaveAnswer={saveAnswer('nonprofit')} onGenerate={generate('nonprofit')} generating={genNpf} />
          </div>
        ) : (
          <div className="mt-10 text-center text-sm text-[#64748B] flex items-center justify-center gap-2"><User size={15} /> Pick a person to begin their combined interview.</div>
        )}
      </div>
    </AdminLayout>
  );
}
